import { HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { tap } from 'rxjs';
import { LicenseStateService } from '../services/license-state-service/license-state-service';
import { AiCharged } from '../../school-license/models/school-license.model';
import { environment } from '../../../environments/environment';

/** Only a write can charge the pool; a read carrying `credits` is the usage log, not a new charge. */
const CHARGING_METHODS = new Set(['POST', 'PUT', 'PATCH']);

/**
 * Notices every AI call that was charged to the school's pool — each one answers with
 * {@link AiCharged} — and refreshes the school list behind it, because `activeLicense` from the
 * last `GET /schools` still carries the balance from before the charge. Without it the dashboard
 * keeps showing the old number until the page is reloaded by hand.
 *
 * Recognised by the shape of the response rather than by a list of URLs: the endpoints that charge
 * are spread over several features and grow with them, and each one already promises this body.
 *
 * The service is resolved lazily for the same reason as in `licenseInterceptor`: it depends on
 * `SchoolService`, and injecting that chain while the interceptor chain is being built would be
 * circular.
 */
export const aiChargeInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);

  // The `AiCharged` contract only holds for our own API; a third-party response that happens to
  // carry a numeric `credits` must not trigger a `GET /schools`.
  if (!CHARGING_METHODS.has(req.method) || !req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  return next(req).pipe(
    tap((event) => {
      if (event.type === HttpEventType.Response && isAiCharged(event.body)) {
        injector.get(LicenseStateService).noteAiCharge();
      }
    }),
  );
};

function isAiCharged(body: unknown): body is AiCharged {
  return (
    !!body && typeof body === 'object' && typeof (body as Partial<AiCharged>).credits === 'number'
  );
}
